/*
Home rubric blocks on phones: a swipeable ring. Native horizontal scroll with
snap does the sliding; the centred block shows its neighbours' edges on both
sides (the edges fade out in CSS). The ring is endless both ways: two edge
clones on each side keep the neighbour peeks filled, and once the scroll
settles on a clone it teleports to the matching real block. Above the phone
breakpoint the blocks are a plain grid and the clones are hidden in CSS.
*/

import afterLoad from './afterLoad.js';
import addEdgeClones, { onScrollSettle } from './edgeClones.js';
import { realTwin } from './loopMath.js';

const PHONE = '(max-width: 739px)';
const CLONES_PER_SIDE = 2;

// Ends the previous ring's settle watch; PJAX swaps the page but keeps the document.
let stopRing = null;

export default function rubricCarousel() {
    const track = document.querySelector('[data-rubric-track]');

    // bfcache brings the page back with its live ring — keep it.
    if (track?.dataset.loopReady) {
        return;
    }
    stopRing?.();
    stopRing = null;
    const real = track ? [...track.children] : [];

    if (real.length <= CLONES_PER_SIDE || !window.matchMedia(PHONE).matches) {
        return;
    }
    track.dataset.loopReady = 'true';
    const releaseCloneImages = addEdgeClones(track, real, {
        perSide: CLONES_PER_SIDE,
        attribute: 'data-rubric-clone',
        holdImages: true,
    });
    const lifetime = new AbortController();

    // After the page's main image: the held clones are needed only once the reader swipes.
    afterLoad(() => {
        if (!lifetime.signal.aborted) {
            releaseCloneImages();
        }
    });
    stopRing = () => lifetime.abort();
    startRing(track, real.length, lifetime.signal);
}

function startRing(track, count, signal) {
    const blocks = [...track.children];
    const jumpTo = (index) => {
        track.scrollTo({ left: centredOffset(track, blocks[index]), behavior: 'auto' });
    };

    // Start on the first real block, with the last one peeking on the left.
    jumpTo(CLONES_PER_SIDE);
    onScrollSettle(track, () => {
        const twin = realTwin(centredIndex(track, blocks), CLONES_PER_SIDE, count);

        if (twin !== null) {
            jumpTo(twin);
        }
    }, signal);
}

function centredOffset(track, block) {
    return block.offsetLeft + (block.offsetWidth / 2) - (track.clientWidth / 2);
}

function centredIndex(track, blocks) {
    const distances = blocks.map((block) => {
        return Math.abs(centredOffset(track, block) - track.scrollLeft);
    });

    return distances.indexOf(Math.min(...distances));
}
